<script lang="ts">
  import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
  import MediaCredit from '../MediaCredit.svelte'
  import MirroredIcon from '../MirroredIcon.svelte'
  import { getI18n } from '../../i18n'
  import { getPageCategory } from '../../pageCategory'
  import type { SerializedImageGalleryBlock } from '../../types'

  let { block, priority = false }: { block: SerializedImageGalleryBlock; priority?: boolean } = $props()

  const i18n = getI18n()
  const pageCategory = getPageCategory()
  const stageSizes = '(max-width: 744px) 100vw, 720px'

  let track = $state<HTMLElement>()
  let slides = $state<HTMLElement[]>([])
  let currentIndex = $state(0)
  // The slide that `show` scrolls to. Until it is in view, callbacks about the slides passed on the way (which can
  // arrive late) must not change the current image.
  let scrollTarget: number | null = null

  const imageCount = $derived(block.images.length)
  const currentImage = $derived(block.images[currentIndex] ?? block.images[0])

  // The stage is a native scroll-snap track; the slide mostly in view is the current one, however it got there.
  $effect(() => {
    if (!track) return

    const observer = new IntersectionObserver(
      (entries) => {
        // Callbacks also fire when a slide drops below the threshold, while it is still partly visible.
        const visible = entries.find((entry) => entry.intersectionRatio >= 0.6)
        if (!visible) return

        const index = slides.indexOf(visible.target as HTMLElement)
        if (scrollTarget !== null && index !== scrollTarget) return

        scrollTarget = null
        currentIndex = index
      },
      { root: track, threshold: 0.6 },
    )
    slides.forEach((slide) => observer.observe(slide))

    return () => observer.disconnect()
  })

  // A swipe can interrupt the scroll before it reaches its target.
  function clearScrollTarget() {
    scrollTarget = null
  }

  // Right-to-left tracks scroll towards negative offsets, and their arrow keys are swapped.
  function inlineDirection(): 1 | -1 {
    return track && getComputedStyle(track).direction === 'rtl' ? -1 : 1
  }

  // Wraps around: next on the last image shows the first one.
  // Scrolls to an explicit offset: `scrollIntoView` does not scroll back to the first slide of a right-to-left track in Chrome.
  function show(index: number) {
    const targetIndex = (index + imageCount) % imageCount
    currentIndex = targetIndex
    if (!track) return

    const left = inlineDirection() * targetIndex * track.clientWidth
    // Without a scroll there are no callbacks that would clear the target.
    scrollTarget = Math.abs(track.scrollLeft - left) < 1 ? null : targetIndex
    track.scrollTo({ left })
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

    event.preventDefault()
    const towardsRight = event.key === 'ArrowRight' ? 1 : -1
    show(currentIndex + towardsRight * inlineDirection())
  }
</script>

<figure class="image-gallery">
  <div class="image-gallery-body">
    <div class="image-gallery-stage">
      <!-- svelte-ignore a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -- the scrollable track is focusable and scrolled with the arrow keys -->
      <div class="image-gallery-track" bind:this={track} tabindex="0" role="group" aria-label={block.caption ?? currentImage.title} onkeydown={handleKeydown} onscrollend={clearScrollTarget}>
        {#each block.images as item, index (index)}
          <div class="image-gallery-slide" bind:this={slides[index]}>
            <img class="image-gallery-photo" src={item.image.src} srcset={item.image.srcset} sizes={stageSizes} width={item.image.width} height={item.image.height} alt={item.altText} loading={index === 0 ? 'eager' : 'lazy'} fetchpriority={priority && index === 0 ? 'high' : undefined} />
          </div>
        {/each}
      </div>

      {#if imageCount > 1}
        <span class="image-gallery-counter" dir="ltr">{currentIndex + 1} / {imageCount}</span>
        <button type="button" class="image-gallery-arrow -previous" aria-label={i18n.t('gallery.previous')} onclick={() => show(currentIndex - 1)}>
          <MirroredIcon icon={faChevronLeft} />
        </button>
        <button type="button" class="image-gallery-arrow -next" aria-label={i18n.t('gallery.next')} onclick={() => show(currentIndex + 1)}>
          <MirroredIcon icon={faChevronRight} />
        </button>
      {/if}
      <MediaCredit placement="bottom" />
    </div>

    {#if imageCount > 1}
      <div class="image-gallery-thumbnails">
        {#each block.images as item, index (index)}
          <button
            type="button"
            class={['image-gallery-thumbnail', {
              '-active': index === currentIndex,
              '-variant-brand': pageCategory.variant === 'brand',
              '-variant-warning': pageCategory.variant === 'warning',
              '-variant-success': pageCategory.variant === 'success',
            }]}
            aria-label={i18n.t('gallery.showImage', { title: item.title })}
            aria-current={index === currentIndex ? 'true' : undefined}
            onclick={() => show(index)}
          >
            <img class="image-gallery-thumbnail-image" src={item.image.src} srcset={item.image.srcset} sizes="120px" width={item.image.width} height={item.image.height} alt="" loading="lazy" />
          </button>
        {/each}
      </div>
    {/if}
  </div>

  <figcaption class="image-gallery-caption">
    <span class="image-gallery-caption-title">{currentImage.title}</span>
    {#if block.caption}
      <span class="image-gallery-caption-text">· {block.caption}</span>
    {/if}
  </figcaption>
</figure>

<style>
  .image-gallery {
    grid-column: full;
    display: grid;
    grid-template-columns: subgrid;
  }

  .image-gallery-body {
    grid-column: full;
    display: grid;
    gap: var(--space-4);

    @media (--tablet) {
      grid-column: content;
    }
  }

  .image-gallery-stage {
    position: relative;
    overflow: hidden;
    background-color: var(--color-gray-95);

    @media (--tablet) {
      border-radius: var(--radius-3);
    }
  }

  .image-gallery-track {
    display: flex;
    aspect-ratio: 3 / 2;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    overscroll-behavior-x: contain;

    @media (prefers-reduced-motion: no-preference) {
      scroll-behavior: smooth;
    }

    &::-webkit-scrollbar {
      display: none;
    }

    &:focus-visible {
      outline: var(--border-2) solid var(--color-variant-brand);
      outline-offset: calc(var(--border-2) * -1);
    }
  }

  .image-gallery-slide {
    flex: none;
    width: 100%;
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }

  .image-gallery-photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .image-gallery-counter {
    position: absolute;
    inset-block-start: var(--space-5);
    inset-inline-end: var(--space-5);
    padding: var(--space-2) var(--space-5);
    border-radius: var(--radius-6);
    background-color: var(--shadow-2);
    color: var(--color-white);
    font-size: var(--font-size-3);
    font-weight: var(--font-weight-3);
    font-variant-numeric: tabular-nums;
    pointer-events: none;
  }

  .image-gallery-arrow {
    position: absolute;
    inset-block-start: 50%;
    translate: 0 -50%;
    display: grid;
    place-items: center;
    width: var(--space-10);
    height: var(--space-10);
    padding: 0;
    border: none;
    border-radius: var(--radius-6);
    background-color: var(--color-white);
    color: var(--color-heading);
    box-shadow: 0 var(--space-1) var(--space-4) var(--shadow-1);
    cursor: pointer;
    opacity: var(--opacity-4);
    transition: opacity var(--transition-medium);

    &:hover,
    &:focus-visible {
      opacity: 1;
    }

    &:focus-visible {
      outline: var(--border-2) solid var(--color-variant-brand);
      outline-offset: var(--border-2);
    }

    @media (--mobile) {
      width: calc(var(--space-9) + var(--space-4));
      height: calc(var(--space-9) + var(--space-4));
    }
  }

  .image-gallery-arrow.-previous {
    inset-inline-start: var(--space-5);
  }

  .image-gallery-arrow.-next {
    inset-inline-end: var(--space-5);
  }

  .image-gallery-thumbnails {
    display: flex;
    gap: var(--space-3);
    padding: var(--space-1) var(--page-gutter);
    overflow-x: auto;
    scrollbar-width: none;

    @media (--tablet) {
      display: grid;
      grid-template-columns: repeat(6, minmax(0, 1fr));
      gap: var(--space-4);
      padding-inline: 0;
      overflow: visible;
    }
  }

  .image-gallery-thumbnail {
    flex: none;
    width: var(--space-15);
    aspect-ratio: 1;
    padding: 0;
    border: none;
    border-radius: var(--radius-2);
    overflow: hidden;
    background-color: var(--color-gray-95);
    cursor: pointer;
    opacity: var(--opacity-2);
    outline: var(--border-2) solid transparent;
    outline-offset: var(--border-2);
    transition:
      opacity var(--transition-medium),
      outline-color var(--transition-medium);

    &:hover {
      opacity: var(--opacity-4);
    }

    @media (--tablet) {
      width: 100%;
      aspect-ratio: 3 / 2;
    }
  }

  .image-gallery-thumbnail.-active {
    opacity: 1;
  }

  .image-gallery-thumbnail.-active.-variant-brand,
  .image-gallery-thumbnail.-variant-brand:focus-visible {
    outline-color: var(--color-variant-brand);
  }

  .image-gallery-thumbnail.-active.-variant-warning,
  .image-gallery-thumbnail.-variant-warning:focus-visible {
    outline-color: var(--color-variant-warning);
  }

  .image-gallery-thumbnail.-active.-variant-success,
  .image-gallery-thumbnail.-variant-success:focus-visible {
    outline-color: var(--color-variant-success);
  }

  .image-gallery-thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .image-gallery-caption {
    grid-column: content;
    display: flex;
    flex-wrap: wrap;
    gap: 0 var(--space-3);
  }

  .image-gallery-caption-title {
    color: var(--color-text);
    font-weight: var(--font-weight-2);
  }

  .image-gallery-caption-text {
    color: inherit;
  }
</style>
